import React from 'react'
import clsx from 'clsx'
import { Snackbar, useTheme } from '@mui/material'
import { Alert } from '@mui/material'
import { useLocation } from 'react-router'
import { useInView } from 'react-hook-inview'
import { Information, MessageText, Mobile, More, Sms } from 'iconsax-react'
import { HiCheck } from 'react-icons/hi'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'
import { AnimatePresence, motion } from 'framer-motion'

import Spacer from '../../atoms/Spacer'
import MediaElement from './MediaElement'
// import TagList from '../../atoms/TagList'
import ButtonMenu from '../ButtonMenu'
// import TagInput from '../TagInput'
import MediaAvatar from '../../atoms/MediaAvatar'
import Tooltip from '../../atoms/Tooltip'
import ReadInfo from './ReadInfo'
import MessageSkeleton from '../MessageSkeleton'
import DeleteMessageModal from '../DeleteMessageModal'
import EmojiSelect from '@SonarCX/components/organisms/ChatWindow/Controls/TextArea/EmojiSelect'
import PopperEmojiPicker from '@SonarCX/components/atoms/EmojiPicker'
import Reaction from './Reaction'
import MessageTask from '../MessageTask'
import YoutubeEmbed from '@SonarCX/components/atoms/YoutubeEmbed'
import TaskModal from '../ChatModals/TaskModal'

import { formatMessageTimeSince } from '@SonarCX/timeUtilities'
import { Routes } from '@SonarCX/Routes'
import { putPatch, createReaction } from '@SonarCX/lib/api'
import {
  arrayToObject,
  formatMessageTooltipDate,
  replaceMentions,
  loadTaggedUsers
} from '@SonarCX/utilities'
import { track } from '@SonarCX/lib/mixpanel'
import { countEmojis } from '@SonarCX/utilities/countEmojis'
import transformContent from '@SonarCX/utilities/transformContent'
import { useAgent } from '@SonarCX/providers/AgentProvider'
import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'
import { useSocket } from '@SonarCX/providers/SocketProvider'
import { selectPresence } from '@SonarCX/slices/presence'
import { useChatEditState } from '@SonarCX/providers/ChatEditStateProvider'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import { useCopyableState } from '@SonarCX/providers/CopyableStateProvider'
import { AddErrorAlert } from '@SonarCX/slices/alert'
import useLongTouch from '@SonarCX/hooks/useLongTouch'
import useStyles from './styles'

const EDIT = 'Edit'
const EDIT_TAGS = 'Edit Tags'
const SAVE = 'Save Changes'
const DISCARD = 'Discard Changes'
const COPY = 'Copy Link'
const REACT = 'Add Reaction'
const COPY_TEXT = 'Copy Text'
const QUOTE = 'Quote Text'
const TASK = 'Create Task'
const DELETE = 'Delete'

const CHANNEL_NAME_MAP = {
  email: 'Email',
  sms_voice: 'SMS'
}

const TYPE_MAP = {
  email: 'Email',
  sms: 'SMS',
  chat: ''
}

const getIcon = (type, isYours, isPeerChat) => {
  const ICON_MAP = {
    sms: Mobile,
    email: Sms
  }

  if (ICON_MAP[type]) {
    return ICON_MAP[type]
  }

  if (type === 'chat' && !isPeerChat) {
    return !isYours
      ? MessageText
      : props => (
          <MessageText style={{ transform: 'scale(-1, 1)' }} {...props} />
        )
  }

  return () => null
}

const Message = (props, ref) => {
  const {
    content,
    sender,
    senderFull,
    media,
    tags = [],
    // allTags,
    type,
    date,
    isPrivateNote,
    id,
    sessionID,
    meta,
    correlationId,
    received,
    expired,
    className,
    lastRead,
    p2p,
    recentActivity,
    above,
    below,
    convo,
    isDirect,
    allowInteraction = true,
    containerID = '#chat',
    hoverChild = null
  } = props

  const dispatch = useAppDispatch()
  // Gets props from ChatWindow
  const location = useLocation()
  const [inViewRef, inView, observer] = useInView({
    root: document.querySelector(containerID),
    rootMargin: '100px',
    threshold: 0
  })
  const theme = useTheme()

  const { currentUser } = useAgent()
  const { findChannels, allUsers, loadUser } = useUserAPI()
  const {
    api: { handlePatch },
    retry: { msgMap }
  } = useSocket()
  const { presence } = useAppSelector(selectPresence)
  const { isSmall } = useBreakpoint()
  const { setText, copyToClipboard } = useCopyableState()
  const {
    edit: startEdit,
    end: endEdit,
    state: chatEditState,
    quote: startQuote
  } = useChatEditState()
  const isBeingEdited = React.useMemo(
    () =>
      chatEditState.originalMessage && chatEditState.originalMessage.id === id,
    [chatEditState, id]
  )
  const [editing, setEditing] = React.useState(isBeingEdited)
  const [editingTags, setEditingTags] = React.useState(isBeingEdited)
  const [hovering, setHovering] = React.useState(false)
  const [showDelete, setShowDelete] = React.useState(false)
  const [showEmojiSelect, setShowEmojiSelect] = React.useState(false)
  const [tagString, setTagString] = React.useState(tags.join(','))
  const [body, setBody] = React.useState(content)
  const [linkCopied, setLinkCopied] = React.useState(false)
  const [user, setUser] = React.useState(null)
  const [online, setOnline] = React.useState(false)
  const messageContentRef = React.useRef()
  const [taggedUsersLoaded, setTaggedUsersLoaded] = React.useState(false)
  const [menuOpen, setMenuOpen] = React.useState(false)
  const [visible, setVisible] = React.useState(false)
  const [showTaskModal, setShowTaskModal] = React.useState(false)
  const { position: mousePosition, ...longTouchProps } = useLongTouch(() => {
    setMenuOpen(true)
    setHovering(false)
  })

  const formattedTime = React.useMemo(() => {
    try {
      return formatMessageTimeSince(new Date(date), Date.now())
    } catch {
      console.warn('Invalid date for Message', date)
      return formatMessageTimeSince(Date.now(), Date.now())
    }
  }, [date])

  const formattedReactions = React.useMemo(() => {
    if (!meta) return []

    if (meta.reactions && meta?.reactions.length === 0) return []

    return (meta.reactions ?? []).reduce((acc, current) => {
      const { userId, user_id, reaction } = current
      const userIdToUse = userId || user_id
      const user = allUsers[userIdToUse] ?? {}

      if (!user) {
        loadUser(userIdToUse)
      }
      if (acc[reaction]) {
        acc[reaction].users.push(user)
        acc[reaction].count++
      } else {
        acc[reaction] = { users: [user], reaction, count: 1 }
      }
      return acc
    }, {})
  }, [meta, allUsers, loadUser])

  const yourID = currentUser()?.id || null
  const isYours = senderFull ? senderFull.id === yourID : true

  const showTime = !below
  const showAvatar = (above && !below) || (!above && !below)
  const middleMsg = above && below
  const startGroup = !above && below
  const endGroup = above && !below

  // Counts how many emojis are in emoji only strings
  const emojiCount = React.useMemo(() => {
    if (!content) {
      return 0
    }

    // Partly from here: https://stackoverflow.com/questions/41079416/javascript-detect-if-a-string-contains-only-unicode-emojis
    const emojiRe = /\p{Extended_Pictographic}/gu
    const removeEmoji = str => str.replace(emojiRe, '')
    const removed = removeEmoji(content).trim()

    // Some emoji strings have only invisible characters left
    const isOnlyEmojis = !removed.length || removed === '‍‍'

    if (isOnlyEmojis) {
      const count = countEmojis(content)
      return count
    }

    return 0
  }, [content])

  const taskLinks = React.useMemo(() => {
    // Find all links that are to a task in the current domain
    const currentDomain = window.location.origin
    const taskMatches = (content ?? '').matchAll(
      new RegExp(
        `(${currentDomain}${Routes.TASKS}/([0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}))`,
        'g'
      )
    )

    // Filter matches to only get the link
    const links = [...taskMatches].flatMap(match => {
      // Only get the id
      if (match.length > 2) {
        return match[2]
      }
      return []
    })

    return links
  }, [content])

  const youtubeEmbeds = React.useMemo(() => {
    // Find links to youtube videos
    const youtubeMatches = (content ?? '').matchAll(
      new RegExp(
        '(https?://)?(www.)?(youtube.com|youtu.?be)/watch\\?v=([a-zA-Z0-9_-]{11})',
        'g'
      )
    )

    // Filter matches to only get the embed id
    const embedIDs = [...youtubeMatches].flatMap(match => {
      // Only get the id
      if (match.length > 4) {
        return match[4]
      }
      return []
    })

    return embedIDs
  }, [content])

  const classes = useStyles({
    flip: isYours,
    middleMsg,
    startGroup,
    endGroup,
    showAvatar,
    smallMedia: isSmall,
    emojiCount,
    isYours
  })

  const read = date < lastRead

  React.useEffect(() => {
    if (!chatEditState.originalMessage) {
      setEditing(false)
      setEditingTags(false)
      return
    }
    if (
      chatEditState.originalMessage &&
      chatEditState.originalMessage.id !== id
    ) {
      setEditing(false)
      setEditingTags(false)
    }
  }, [chatEditState.originalMessage, id])

  React.useEffect(() => {
    setUser(allUsers[senderFull?.id])
  }, [allUsers, senderFull])

  // Loads all tagged users from a message
  React.useEffect(() => {
    if (taggedUsersLoaded) {
      return
    }

    const message = content ?? ''
    loadTaggedUsers(message, loadUser)

    setTaggedUsersLoaded(true)
  }, [taggedUsersLoaded, content, loadUser])

  // checking p2p chats if other user is online
  React.useEffect(() => {
    if (p2p && sessionID && presence[sessionID]) {
      setOnline(true)
    }
  }, [sessionID, p2p, presence, online])

  // Add edited to the end of the markdown
  const edited = React.useMemo(
    () => (meta?.body_history?.length ?? 0) !== 0,
    [meta]
  )

  const options = React.useMemo(() => {
    let opts = []

    if (editing || editingTags) {
      opts = [SAVE, DISCARD]
    } else if (isYours) {
      opts = [EDIT, ...opts]
      if (location.pathname.startsWith(Routes.PEER_CHAT)) {
        opts = [...opts, DELETE]
      }
    } else {
      // opts = [EDIT_TAGS, ...opts]
    }

    if (!editing && !editingTags) {
      opts = [...opts, QUOTE, COPY_TEXT, REACT, TASK]
    }

    return opts
  }, [location, editing, editingTags, isYours])

  // Reset hovering state
  React.useEffect(() => {
    setHovering(false)
  }, [menuOpen])

  // Stores content for copying
  React.useEffect(() => {
    if (!content) {
      setText('')
    } else {
      const agents = Object.values({
        ...allUsers,
        ...arrayToObject(convo?.fullUsers ?? [])
      })
      const transformed = replaceMentions(content, agents)
      const withoutMultipleSpaces = transformed.replace(/ +/gm, ' ')
      setText(withoutMultipleSpaces)
    }
  }, [content, allUsers, convo, setText])

  // See if message should be shown, sometimes hook breaks when switching rapidly between sessions
  // eslint-disable-next-line
  React.useEffect(() => {
    if (visible || inView) {
      return
    }

    if (!observer) {
      return
    }

    // Check if the message is within chat timeline
    const rect = observer.target?.getBoundingClientRect()
    const chat = document.querySelector('#chat')?.getBoundingClientRect()
    if (rect && chat) {
      const inside = chat.top <= rect.y && rect.y <= chat.bottom

      if (inside) {
        setVisible(true)
      }
    }
  })

  const handleTaskClick = React.useCallback(() => {
    setShowTaskModal(true)
  }, [])

  const handleTaskClose = React.useCallback(() => {
    setShowTaskModal(false)
  }, [])

  const handleTaskCreation = React.useCallback(
    task => {
      const taskURL = `${window.location.origin}${Routes.TASKS}/${task.id}`
      const msg = `> ${content} \n\nCreated a task: ${taskURL}`
      startQuote(msg)
      setTimeout(() => {
        document.querySelector('#chat-input').focus()
      }, 0)
    },
    [content, startQuote]
  )

  if (media?.length === 0 && !content) {
    return null
  }

  const pushChanges = () => {
    const updatedData = { content: { body: content }, tags }

    if (editing) {
      const contentBody = body?.trim() || null
      updatedData.content = { body: contentBody }
    }

    const newTags = tagString
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0)
    const uniqueTags = [...new Set(newTags)]
    updatedData.tags = uniqueTags

    const data = { ...updatedData, action: 'chat' }
    putPatch(id, data)
      .then(data => {
        setEditing(false)
        setEditingTags(false)
        endEdit()
        handlePatch(data)
      })
      .catch(err => {
        console.error('Error putting patch', err)
        dispatch(AddErrorAlert({ err }))
      })
  }

  const copyLinkToClipboard = () => {
    const link = `${window.location.href}?session=${sessionID}&id=${id}`
    navigator.clipboard.writeText(link).then(() => setLinkCopied(true))
  }

  const handleMenuSelect = item => {
    if (item === EDIT) {
      setBody(content)
      //setTagString(tags.join(','))
      setEditing(true)
      startEdit(id)
      setTimeout(() => {
        document.querySelector('#chat-input').focus()
      }, 0)
    } else if (item === EDIT_TAGS) {
      setTagString(tags.join(','))
      setEditingTags(true)
    } else if (item === SAVE) {
      pushChanges()
    } else if (item === DISCARD) {
      setEditing(false)
      setEditingTags(false)
      endEdit()
    } else if (item === COPY) {
      copyLinkToClipboard()
    } else if (item === COPY_TEXT) {
      copyToClipboard()
    } else if (item === DELETE) {
      setShowDelete(true)
    } else if (item === REACT) {
      setShowEmojiSelect(true)
    } else if (item === QUOTE) {
      const msg = `> ${content} \n\n`
      startQuote(msg)
      setTimeout(() => {
        document.querySelector('#chat-input').focus()
      }, 0)
    } else if (item === TASK) {
      handleTaskClick()
    }

    setMenuOpen(false)
  }

  const handleLinkCopyClose = () => setLinkCopied(false)

  const messageClass = isPrivateNote ? classes.note : classes.message

  let sideChannelElem = null
  let sideChannelString = null
  if (!isPrivateNote) {
    let sideChannels = []
    if (meta?.side_channels?.length > 0) {
      sideChannels = meta.side_channels
      // } else {
      //   const sender = allUsers[senderFull.id];
      //   if (sender?.channels?.length > 0) {
      //     sideChannels = sender.channels.map(({ id }) => id);
      //   }
    }

    if (sideChannels.length > 0) {
      const channelsObj = findChannels(sideChannels.map(({ id }) => id))
      if (channelsObj.length > 0) {
        const channelsString = channelsObj.map(({ name, channel }, i) => (
          <div key={i} className='channel'>
            {i !== 0 && <>, by </>}
            {CHANNEL_NAME_MAP[channel.type]} to
            <div className='name'>{name}</div>
          </div>
        ))
        sideChannelString = `Sent by ${channelsObj
          .map(
            ({ name, channel }) =>
              `${CHANNEL_NAME_MAP[channel.type]} to ${name}`
          )
          .join(', by ')}`
        sideChannelElem = (
          <div className={classes.channels}>
            <div>Sent by</div> {channelsString}
          </div>
        )
      }
    }
  }

  // get mapped objects from socket
  const msgPayload = [...msgMap.current.values()]

  // finding index of msg to retry / replace
  const getIndex = () => {
    return msgPayload.findIndex(
      msg => msg.payload.correlationId === correlationId
    )
  }

  const restartTimer = () => {
    const index = getIndex()

    msgPayload[index].count = 0

    const exist = msgMap.current.get(correlationId)

    msgMap.current.set(correlationId, {
      ...exist,
      timer: setInterval(() => {
        exist['timer function'](correlationId)
      }, 5000)
    })
    return
  }

  // showing message received ticks
  const ticks = () => {
    if (!received || expired) {
      return (
        <span className={classes.ticks}>
          <span className={classes.topTick}></span>
          <span className={classes.bottomTick}>
            <HiCheck />
          </span>
        </span>
      )
    } else if (received) {
      return twoTicks()
    }
  }

  const twoTicks = () => {
    if ((p2p && !isYours) || recentActivity) {
      return null
    } else {
      return (
        <span
          className={classes.ticks}
          style={{ color: read ? theme.palette.primary.main : '#aaa' }}
        >
          <span className={classes.topTick}>
            <HiCheck />
          </span>
          <span className={classes.bottomTick}>
            <HiCheck />
          </span>
        </span>
      )
    }
  }

  const retryError = () => {
    const index = getIndex()

    if (msgPayload[index]?.count === 2) {
      return (
        <div className={classes.errorMsg}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginLeft: '0.25rem',
              marginRight: '0.25rem'
            }}
          >
            <Information
              variant='Bold'
              style={{ marginRight: '0.25rem', width: '1rem', height: '1rem' }}
            />
            <span style={{ fontWeight: theme.bold.bolder }}>Message error</span>
          </div>
          {msgPayload[index]?.count === 2 && (
            <div onClick={() => restartTimer()} className={classes.retry}>
              Retry
            </div>
          )}
        </div>
      )
    }
  }

  const handleEmojiSelect = async emoji => {
    try {
      setHovering(false)
      const { native } = emoji
      track('REACTION_ADDED', { emoji: native })
      await createReaction(id, native)
    } catch (error) {
      console.error('Error adding reaction', error)
    }
  }

  const handleEmojiRemove = async () => {
    // Todo: ask how to remove reaction (api doesnt support empty string)
    // try {
    //   await reactionEvent(id, '')
    // } catch (error) {
    //   console.error('Error removing reaction', error)
    // }
  }

  const isPeerChat = isDirect && !convo?.isGroup

  const Icon = getIcon(type, isYours, isPeerChat)

  const info = (
    <ReadInfo
      date={date}
      session={sessionID}
      sender={senderFull?.id}
      isPeerChat={isPeerChat}
    />
  )
  const ttContent = (
    <div className={classes.tooltip}>
      {isPeerChat ? (isYours ? info : null) : info}

      <div>Sent on {formatMessageTooltipDate(date)}</div>

      {sideChannelString && <div>{sideChannelString}</div>}
    </div>
  )
  const formattedSender = user && user.name !== sender ? user.name : sender

  return (
    <>
      <div
        className={clsx(
          classes.root,
          className,
          middleMsg && classes.middleMsg,
          startGroup && classes.start,
          endGroup && classes.end
        )}
        ref={ref}
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        {...longTouchProps}
      >
        <div className={classes.row} ref={inViewRef}>
          {inView || visible ? (
            <>
              <MediaAvatar
                user={user ?? senderFull}
                className={classes.avatar}
                style={{ visibility: showAvatar ? 'visible' : 'hidden' }}
                endPosition={showAvatar && endGroup}
                hideCard={!allowInteraction}
                disableAnimation
              />

              <div className={clsx(classes.messageBase, messageClass)}>
                <div className='box'>
                  <Spacer />

                  <div>
                    {(content || meta?.body_history?.length > 0) && (
                      <div
                        ref={messageContentRef}
                        className={classes.msgContent}
                        style={{ width: editingTags ? '200px' : 'unset' }}
                      >
                        {content
                          ? transformContent(
                              content,
                              classes.mention,
                              {
                                ...allUsers,
                                ...arrayToObject(convo?.fullUsers ?? [])
                              },
                              currentUser()?.name,
                              classes.mentionYou,
                              isYours
                            )
                          : ''}
                      </div>
                    )}

                    <MediaElement
                      media={media}
                      observer={observer}
                      isYours={isYours}
                    />

                    {/* {editing || editingTags ? (
                      <TagInput
                        id={id}
                        value={tagString}
                        onChange={e => setTagString(e.target.value)}
                        label='Enter tags'
                        tags={allTags}
                      />
                    ) : (
                      <TagList tags={tags} />
                    )} */}
                  </div>
                </div>
              </div>

              {hovering && hoverChild}

              {allowInteraction && (
                <>
                  {hovering && (
                    <EmojiSelect
                      size={theme.size.sm}
                      additionalClasses={classes.emojiAction}
                      handleEmojiSelect={handleEmojiSelect}
                    />
                  )}

                  <ButtonMenu
                    id={`message-select-${id}`}
                    options={options}
                    onSelect={handleMenuSelect}
                    className={classes.dots}
                    manual
                    open={menuOpen}
                    onOpen={() => setMenuOpen(true)}
                    onClose={() => setMenuOpen(false)}
                    anchorReference={
                      mousePosition ? 'anchorPosition' : 'anchorEl'
                    }
                    anchorPosition={{
                      left: (mousePosition?.x ?? 0) - 50,
                      top: (mousePosition?.y ?? 0) - 25
                    }}
                  >
                    {!mousePosition && (
                      <More
                        size={theme.size.sm}
                        className='dot'
                        style={{
                          transform: 'rotate(90deg)',
                          visibility:
                            hovering && options.length > 0
                              ? 'visible'
                              : 'hidden'
                        }}
                      />
                    )}
                  </ButtonMenu>
                </>
              )}

              <Spacer />
            </>
          ) : (
            <MessageSkeleton isYours={isYours} />
          )}
        </div>
        <AnimatePresence>
          {Object.values(formattedReactions).length > 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={clsx(classes.row, classes.bottomRow)}
            >
              <div className={clsx('reactions', classes.reactionsRoot)}>
                {Object.values(formattedReactions).map((reaction, i) => (
                  <Reaction
                    key={`reaction-${i}-${id}`}
                    reaction={reaction}
                    handleEmojiRemove={handleEmojiRemove}
                  />
                ))}
              </div>
              <Spacer />
            </motion.div>
          )}
        </AnimatePresence>
        <div>
          {showTime && (
            <div className={clsx(classes.row, !isSmall && classes.bottomRow)}>
              <div className={clsx(classes.messageBase, messageClass)}>
                <div className='footer'>
                  <Tooltip
                    title={ttContent}
                    placement='bottom'
                    disabled={!allowInteraction}
                    arrow
                  >
                    <div className='info'>
                      {/* Don't show on peer chats */}
                      {!isPeerChat && (
                        <div className='name'>
                          {isYours
                            ? `${formattedSender} (Me)`
                            : formattedSender}
                        </div>
                      )}

                      <div className='rest'>
                        <div>
                          <Icon className={classes.icon} />
                          {isPrivateNote ? 'Note' : type && TYPE_MAP[type]}{' '}
                          {formattedTime}
                        </div>

                        {correlationId ? ticks() : twoTicks()}
                        {edited && (
                          <div className={classes.edited}>(edited)</div>
                        )}
                      </div>
                    </div>
                  </Tooltip>
                </div>

                <div>{retryError()}</div>
              </div>

              <Spacer />
            </div>
          )}

          {sideChannelElem && showTime && (
            <div className={clsx(classes.row, !isSmall && classes.bottomRow)}>
              <div className={clsx(classes.messageBase, messageClass)}>
                {sideChannelElem}
              </div>
            </div>
          )}
        </div>
      </div>

      {linkCopied && (
        <Snackbar
          open={linkCopied}
          autoHideDuration={4000}
          onClose={handleLinkCopyClose}
        >
          <Alert
            severity='success'
            variant='filled'
            onClose={handleLinkCopyClose}
          >
            Link copied to clipboard.
          </Alert>
        </Snackbar>
      )}

      {showDelete && (
        <DeleteMessageModal
          onClose={() => setShowDelete(false)}
          // Make message display as a single message
          messageProps={{ ...props, above: false, below: false }}
        />
      )}

      {showEmojiSelect && (
        <PopperEmojiPicker
          setOpen={setShowEmojiSelect}
          handleEmojiSelect={handleEmojiSelect}
        />
      )}

      {allowInteraction &&
        taskLinks.map(taskId => <MessageTask key={taskId} id={taskId} />)}

      {allowInteraction &&
        youtubeEmbeds.map((id, i) => (
          <YoutubeEmbed key={id} embedID={id} isYours={isYours} />
        ))}

      {showTaskModal && (
        // @ts-ignore
        <TaskModal
          open
          onClose={handleTaskClose}
          onTaskCreated={handleTaskCreation}
          canEdit
          canUpdate
          singleCreate
          viewAfterCreate={false}
          assignVals={{
            title: new Date(date).toLocaleDateString(),
            content: `<p class="editor-paragraph" dir="ltr"><span style="white-space: pre-wrap;">${content}</span></p>`
          }}
        />
      )}
    </>
  )
}

export default React.forwardRef(Message)
